import { useCallAppHandle } from "@/hooks/useBridge";
import router from "@/router";
import Big from "big.js";
import { useDateFormat } from "@vueuse/core";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { store } from "@/pinia/store";
import { DictResType } from "@/api/global/dict/types";
import { sha256 } from "js-sha256";
import { useShowToast } from "@/hooks/useVant";
import { h5Url } from '@/config'

const utilGetImage = (name: string) => {
  return new URL(`../assets/image/${name}`, import.meta.url).href;
};

// 判断是否支持webp
const isSupportWebp = () => {
  const storeData = store()
  if (utilIsIos()) {
    const bv = (storeData.appData.cmeta && storeData.appData.cmeta !== '' && storeData.appData.cmeta['ov'] && storeData.appData.cmeta['ov']) !== '' ? storeData.appData.cmeta['ov'].toLocaleLowerCase().replace('ios ', '') : ''
    return bv === '' || (bv !== '' && parseFloat(bv.split('.')[0]) > 14)
  } else {
    return true
  }
}

const utilShowImage = (
  imgUrl: string,
  cutType = "?x-oss-process=image/format,webp"
) => {
  console.log('查看是否支持webp', isSupportWebp())
  //图片压缩后缀
  if (
    !!imgUrl &&
    !imgUrl.includes("https://thirdwx.qlogo.cn") &&
    !imgUrl.includes("!watermark") &&
    isSupportWebp()
  ) {
    return `${imgUrl}${cutType}`;
  } else {
    return imgUrl;
  }
};

const utilGetUrlData = (name: string) => {
  var reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)", "i");
  var r = (
    decodeURIComponent(window.location.search) ||
    decodeURIComponent(window.location.hash).substr(
      decodeURIComponent(window.location.hash).indexOf("?")
    )
  )
    .substr(1)
    .match(reg);
  if (r != null) return unescape(r[2]);
  return null;
};

const utilIsIos = () => {
  return (
    !!navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/) && utilIsApp()
  ); //ios终端
};

const utilIsAndroid = () => {
  var u = navigator.userAgent;
  return (u.indexOf("Android") > -1 || u.indexOf("Adr") > -1) && utilIsApp(); //android终端
};

const utilIsFlutter = () => {
  return utilGetUrlData("isFlutter") === "1";
};

const utilIsApp = () => {
  if (utilIsFlutter()) return true
  return utilGetUrlData("isApp") === "1";
};

const utilGoMoreLevel = (val: number) => {
  if (utilGetUrlData("isFirstPage") === "1") {
    if (utilIsApp()) {
      useCallAppHandle("back");
    }
  } else {
    router.go(val)
  }
}

/**
 * @description: 返回方法，包含h5端和app端的返回
 * @param {*} isGoBackAppIndex:是否返回app的首页，默认false
 * @return {*}
 */
const utilGoBack = (isGoBackAppIndex = false) => {
  if (utilGetUrlData("isFirstPage") === "1") {
    if (utilIsApp()) {
      if (isGoBackAppIndex) {
        useCallAppHandle("back", 0);
        return;
      }
      useCallAppHandle("back");
    }
  } else {
    router.back();
  }
};

const goBack = () => {
  if (utilIsApp()) {
    useCallAppHandle("back");
  } else {
    router.back();
  }
}

const utilPush = (path = "/", query: GlobalUrlQueryType = {}) => {
  let queryClone = { ...query };
  if (utilIsApp()) {
    queryClone.isApp = "1";
  }
  const timeTemp = utilGetUrlData("timeTemp");
  if (timeTemp) {
    queryClone.timeTemp = timeTemp;
  }
  router.push({
    path,
    query: queryClone,
  });
};

const utilIsPC = () => {
  return utilGetUrlData('isPC') === '1'
}

const getPercent = (numerator: number, denominator: number, fixed = 2) => {
  if (denominator === 0) return 0
  return parseFloat(((numerator / denominator) * 100).toFixed(fixed))
}

const utilReplace = (path = "/", query: GlobalUrlQueryType = {}) => {
  let queryClone = { ...query };
  if (utilIsApp()) {
    queryClone.isApp = "1";
  }
  const timeTemp = utilGetUrlData("timeTemp");
  if (timeTemp) {
    queryClone.timeTemp = timeTemp;
  }
  router.replace({
    path,
    query: queryClone,
  });
};

/**
 * @description: 加减乘除计算方法
 * @param {number} numberArr 要计算的数组
 * @param {GlobalMathType} type 计算类型
 * @return {number}
 */
const utilMath = (numberArr: number[], type: GlobalMathType = "plus") => {
  let _firstNum = new Big(numberArr[0]);
  numberArr.slice(1).forEach((item) => {
    _firstNum = _firstNum[type](item);
  });
  return Number(_firstNum);
};

/**
 * @description: 生成reactive数组
 * @param {Array} newArr 普通数组
 * @param {Array} reactiveArr 要生成的reactive新数组
 */
const utilResetArr = (newArr: any[], reactiveArr: any[]) => {
  //reactive数组循环添加属性
  reactiveArr.length = 0;
  newArr.forEach((item) => {
    reactiveArr.push(item);
  });
};

/**
 * @description: 生成reactive对象
 * @param {object} newObj 普通对象
 * @param {object} reactiveObj 要生成的reactive新对象
 * @param {boolean} isClean 是否清空旧对象
 */
const utilResetObj = (newObj: {}, reactiveObj: {}, isClean = false) => {
  if (isClean) {
    for (const key in reactiveObj) {
      delete reactiveObj[key];
    }
  }
  for (const key in newObj) {
    reactiveObj[key] = newObj[key];
  }
};

/**
 * @param  stamp 需要转换的原时间
 * @param  type 要转换的格式
 * @returns
 */
const utilDateFormat = (stamp: any, type = 1) => {
  if (!stamp) {
    return "";
  }
  const obj = {
    1: "YYYY-MM-DD HH:mm:ss",
    2: "YYYY-MM-DD HH:mm",
    3: "YYYY-MM-DD",
    4: "HH:mm:ss",
    5: "MM-DD",
    6: "MM.DD",
    7: "HH:mm",
    8: "MM-DD HH:mm",
    9: "YYYYMMDDHHmmss",
    10: "YYYY-MM",
    11: "YYYY-MM-01",
  };
  const stampStr = stamp + "";
  let time = new Date(stamp).getTime();
  if (stampStr.indexOf("T") > -1) {
    const stampFilter = stamp
      .substring(0, stampStr.indexOf("."))
      .replace("T", " ")
      .replace(/-/g, "/");
    time = new Date(stampFilter).getTime() + 1000 * 60 * 60 * 8;
  }
  return useDateFormat(time, obj[type]).value;
};

/**
 * @description:姓名过滤
 * @param {string} str 需要过滤的内容
 * @returns
 */
const utilFilterName = (str: string) => {
  if (str) {
    return str.substr(0, 1) + "**";
  }
  return "";
};

/**
 * @description:银行卡号过滤
 * @param {string} str 需要过滤的内容
 * @returns
 */
const utilFilterCardNumber = (str: string) => {
  if (str) {
    const reg = /^(\d{4})\d+(\d{4})$/;
    let newStr = str.replace(reg, "**** **** **** $2");
    return String(newStr);
  }
  return "";
};

//获取浏览器唯一标识
const utilGetFpPromise = () => {
  return new Promise(async (resolve) => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const visitorId = result.visitorId;
    resolve(visitorId);
  });
};

/**
 * 获取字典数据
 * @param {string} code 编码（1001，1002）
 * @returns {Array} 字典数据
 */
const utilGetDictList = (code: string) => {
  if (!code) {
    return [];
  }
  const storeData = store();
  const dictData = storeData.allDict as DictResType[];
  const newDict = dictData.find((item) => item.code == code);
  if (newDict) {
    return newDict.infoList;
  } else {
    return [];
  }
};
/**
 * 验证手机号
 * @param {string} mobile 手机号
 * @returns {Boolean}
 */
const utilRegTel = (mobile: string) => {
  const regTel = /^1[3456789]\d{9}$/; //手机号校验
  return !regTel.test(mobile);
};

/**
 * 截取视频封面图
 * @param {string} relativePic 封面图url
 * @returns {string}
 */
const utilShowVideoUrl = (relativePic: string) => {
  return `${relativePic}?x-oss-process=video/snapshot,t_1000,f_jpg,w_0,h_0,m_fast,ar_auto`;
};

/**
 * 图片压缩后缀
 * @param {string} pic 封面图url
 * @returns {string}
 */
const utilShowImageUrl = (
  pic: string,
  cutType: string = "?x-oss-process=image/format,webp"
) => {
  console.log('查看是否支持webp', isSupportWebp())
  if (
    !!pic &&
    !pic.includes("https://thirdwx.qlogo.cn") &&
    !pic.includes("!watermark") &&
    isSupportWebp()
  ) {
    return `${pic}${cutType}`;
  } else {
    return pic;
  }
};

/**
 * 上传最大值
 * @returns {Array} //array[0],最大多少M array[1],最长多少s
 */
const utilMaxUpload = () => {
  const filterList = utilGetDictList("1031");
  const maxParams = filterList.filter((a) => a.value === "1032043")[0];
  console.log("maxParams", maxParams);
  return maxParams.label.split("_");
};

// 复制文本功能
const copyText = async (str: string) => {
  if (utilIsApp()) {
    if (await checkAppHandle('copyContent')) {
      useCallAppHandle("copyContent", str)
    }
  }
};

// 跳转登录页面
const utilGoLogin = () => {
  const storeData = store()
  console.log('退出登录逻辑处理开始')
  console.log('能否刷新token的值', storeData.isRefreshAppInputToken)
  if (utilIsApp()) {

    if (storeData.isShowNewSource) {
      console.log('新包~~~')
      if (!storeData.isRefreshAppInputToken) {
        console.log('可以调刷新~~~')
        useCallAppHandle('goLogin', window.location.href, (res) => {
          storeData.isRefreshAppInputToken = true
          console.log('goLogin返回', res)
          if (res && (res === 1 || res === '1')) {
            // router.go(0)
            window.location.reload()
          }
        })
      }
    } else {
      useCallAppHandle('goLogin')
    }
  }
};

// 跳转首页
const utilGoHome = (type) => {
  if (utilIsApp()) {
    useCallAppHandle("backToHome", type);
  } else {
    router.go(-1)
  }
};

// 退出登录
const utilGoLogOut = () => {
  if (utilIsApp()) {
    useCallAppHandle("toLogout");
  } else {
    utilReplace('/')
  }
};

// 提现密码md5加密
const encryptionPwd = (val: string) => {
  const password = sha256(val);
  return password;
};

// 校验原生端是否有某个方法
const checkAppHandle = async (appMethod) => {
  return new Promise((resolve) => {
    useCallAppHandle('checkAppHandle', appMethod, (res: any) => {
      console.log('是否有方法', appMethod)
      console.log(res)
      console.log(res == 1)
      resolve(res == 1)
    })
  })
}

// 截取url
const utilInterceptUrl = (url: string) => {
  if (!url) return ''
  let path = url
  if (path.indexOf('http') > -1) {
    if (path.indexOf('https') > -1) {
      path = path.replace('https://', '')
    } else {
      path = path.replace('http://', '')
    }
    return path.substring(path.indexOf('/') + 1, path.indexOf('?') > -1 ? path.indexOf('?') : path.length)
  } else {
    return path.substring(0, path.indexOf('?') > -1 ? path.indexOf('?') : path.length)
  }
}

const getNavHeight = (tem = '51px', appendHeight = 0) => {
  if (utilIsApp()) {
    let initNav = 0
    useCallAppHandle('iphoneStatusBarHeight', '', res => {
      initNav = Number(res)
      console.log('获取顶部状态栏高度***', res, initNav)
    })
    tem = initNav ? (initNav + 51) + 'px' : (40 + 51) + 'px'
  }
  if (appendHeight) {
    tem = (parseInt(tem) + appendHeight) + 'px'
  }
  const paddingNav = `calc(100vh - ${tem})`
  return paddingNav
}

const utilGetNavOfApp = (tem = '51px', appendHeight = 0) => {
  const storeData = store();
  if (utilIsApp()) {
    tem = storeData.phoneStatusBarHeight + parseInt(tem) + 'px'
  }
  if (appendHeight) {
    tem = (parseInt(tem) + appendHeight) + 'px'
  }
  return tem
}

const getContainerHeight = (navHeight = 50, appendHeight = 0) => {
  const storeData = store();
  const tem = utilIsApp() ? storeData.phoneStatusBarHeight || 40 : 0
  return `calc(100vh - ${tem + navHeight + appendHeight}px)`
}

//http转为https
const editUrlOfIos = (url) => {
  //为ios环境下，http转换为https开头，解决ios端图片不展示问题
  let editUrl = url
  if (utilIsIos() && url && !url.includes('https')) {
    editUrl = editUrl.replace('http', 'https')
  }
  return editUrl
}

//时间格式转化-多久之前
const dateLongAgo = (stringTime) => {
  let str = stringTime
  //将字符串转换成时间格式
  let timePublish = new Date(str).getDate()
  let timeNow = new Date().getDate()
  console.log(timePublish)
  console.log(timeNow)
  let diffValue = timeNow - timePublish
  let diffDay = diffValue
  let diffYear = diffValue / 365
  let result = null
  if (diffYear > 1) {
    result = parseInt(diffYear.toString()) + '年前'
  } else if (diffDay >= 1) {
    result = parseInt(diffDay.toString()) + '天前'
  } else {
    result = '今天'
  }
  return result
}

// xss文本过滤
const xssFormat = (str = "", option = {}) => {
  return str
  // xss(str, {
  //   whiteList: {},
  //   ...option
  // })
}

// 获取定位
const getAppLocation = () => {
  return new Promise((resolve) => {
    useCallAppHandle('shareLocation', '', async (loc: any) => {
      console.log('获取定位', loc)
      resolve(utilIsAndroid() ? JSON.parse(loc) : loc)
    })
  })
}

// 评论姓名过滤
const filterName = (str) => {
  if (str) {
    return str.substr(0, 1) + '***' + str.substr(-1, 1)
  }
  return ''
}

//计算2个时间相差的天数，时间戳计算
const dateDifference = (sDate1, sDate2 = new Date().getTime()) => {
  //sDate1和sDate2是时间戳
  let dateSpan = sDate2 - sDate1
  dateSpan = Math.abs(dateSpan)
  let iDays = Math.floor(dateSpan / (24 * 3600 * 1000))
  return iDays
}

//时间戳转日期时间
const dateFormat = (stamp, type = 1, isArr = false) => {
  // 1:年月日 时分秒
  // 2:年月日 时分
  // 3:年月日
  if (!stamp) {
    return ''
  }
  let obj = {
    1: 'YYYY-MM-DD HH:mm:ss',
    2: 'YYYY-MM-DD HH:mm',
    3: 'YYYY-MM-DD',
    4: 'YYYY-MM-01',
    5: 'YYYY-MM-DD HH:mm',
    6: 'HH:mm',
    7: 'YYYY-MM',
    8: 'YYYY'
  }
  const stampStr = stamp + ''
  let time = new Date(stamp).getTime()
  if (stampStr.indexOf('T') > -1) {
    const stampFilter = stamp
      .substring(0, stampStr.indexOf('.'))
      .replace('T', ' ')
      .replace(/-/g, '/')
    time = new Date(stampFilter).getTime() + 1000 * 60 * 60 * 8
  }
  if (type === 5) {
    let dayStr = useDateFormat(time, obj[type]).value
    let dateArr = dayStr.split(' ')
    let dayArr = dateArr[0].split('-')
    let timeArr = dateArr[1].split(':')
    if (isArr) return dayArr
    return dayArr[1] + '月' + dayArr[2] + '日' + timeArr[0] + '点'
  } else if (type === 7) {
    let dayStr = useDateFormat(time, obj[type]).value
    let dateArr = dayStr.split('-')
    if (isArr) return dateArr
  } else {
    return useDateFormat(time, obj[type]).value
  }
}
/**
 * 发送验证码倒计时
 * @param {*} params 定时器对象
 * @param {*} type type=1,短信验证码,type=2语音验证码
 */
const timeCount = (params = { isDisabled: false, time: 60, codeText: '获取验证码', timer: null }, type = 1) => {
  clearInterval(params.timer)
  params.isDisabled = true
  params.timer = setInterval(() => {
    params.time--
    params.codeText = type === 1 ? `(${params.time})重新获取` : `语音验证码已发送(${params.time}）`
    if (params.time < 1) {
      params.codeText = type === 1 ? '获取验证码' : '获取语音验证码'
      params.isDisabled = false
      clearInterval(params.timer)
    }
  }, 1000)
}


//类似url?后面的str转为对象
const parseQueryString = (str, splitType = '&') => {
  const arr = str.split(splitType);
  let obj = {};
  for (let i = 0; i < arr.length; i++) {
    let arr2 = arr[i].split('=');
    // arr2的第一个成员做属性, 第二个成员做值
    obj[arr2[0]] = arr2[1];
  }
  return obj;
}

//复制文字
const toCopy = (str) => {
  let copyInput = document.createElement('input') //创建input元素
  document.body.appendChild(copyInput) //向页面底部追加输入框
  copyInput.setAttribute('value', str) //添加属性，将url赋值给input元素的value属性
  copyInput.select() //选择input元素
  document.execCommand('Copy') //执行复制命令
  useShowToast('复制成功') //弹出提示信息，不同组件可能存在写法不同
  //复制之后再删除元素，否则无法成功赋值
  copyInput.remove() //删除动态创建的节点
}

//动态加载js
const loadScript = (jsfile: string) => {
  if (!isInclude(jsfile)) {
    console.log('****************', jsfile)
    return new Promise((resolve, reject) => {
      var head = document.getElementsByTagName('head')[0];
      var script = document.createElement('script') as any;
      script.type = 'text/javascript';
      console.log(script)
      console.log('head', head)
      script.src = jsfile;
      head.appendChild(script);
      console.log('script', script.src)
      script.onload = script.onreadystatechange = function () {
        if (!this.readyState || this.readyState === "loaded" || this.readyState === "complete") {
          script.onload = script.onreadystatechange = null;
          resolve(true)
        } else {
          reject()
        }
      };
    })
  }
}
const isInclude = (name) => {
  const js = /js$/i.test(name);
  const es = document.getElementsByTagName(js ? 'script' : 'link');
  for (var i = 0; i < es.length; i++) {
    if (es[i][js ? 'src' : 'href'].indexOf(name) != -1) {
      return true
    }
  }
  return false;
}

/**
 * @description 自定义重定向浏览器跳转方法
 * @param {string} path 跳转路由，已自带/#，根据路由模式来
 * @param {object} query url带的参数转换
 *
 */
const locationPush = (path = "", query = {}) => {
  const urlAppCode = utilGetUrlData("appCode") || utilGetUrlData("state")
  try {
    let search = `${h5Url}${path}`
    if (Object.keys(query).length > 0) {
      search += "?"
      for (let key in query) {
        search += `${key}=${query[key]}&`
      }
      search = search.substring(0, search.length - 1)
      if (urlAppCode && urlAppCode != "null" && !search.includes("appCode")) {
        search += `&appCode=${urlAppCode}`
      }
    } else {
      if (urlAppCode && urlAppCode != "null") {
        search += `?appCode=${urlAppCode}`
      }
    }
    location.href = search
  } catch (error) {
    console.log("locationPush错误", error)
  }
}

const httpFormat = (str) => {
  // http开头的图片链接转化为https开头
  if (
    str &&
    str.includes('http://oss') &&
    !str.includes('https') &&
    !str.includes('192.') &&
    !str.includes('127.') &&
    !str.includes('localhost')
  ) {
    return str.replace(/http/g, 'https')
  } else {
    return str
  }
}

//判断是否是微信浏览器的函数
const isWeiXin = () => {
  //window.navigator.userAgent属性包含了浏览器类型、版本、操作系统类型、浏览器引擎类型等信息，这个属性可以用来判断浏览器类型
  return /MicroMessenger/i.test(window.navigator.userAgent) || isOutweb()
}

const isOutweb = () => {
  //判断是否是外部app内的webview打开
  return Number(utilGetUrlData('isOutweb')) == 1
}

/**
 * 获取code
 * @param {string} url  重定向路由
 * @param {*} scope 授权方式 1.snsapi_userinfo 用户确认  2.snsapi_base 静默授权
 */
const getWxCode = (url = '/myModule/login/middle', scope = 'snsapi_userinfo', state = utilGetUrlData("appCode") || 'WY') => {
  const local = h5Url + url
  const oauthUrl = `
  http://127.0.0.1:9/connect/oauth2/authorize?appid=wxdeda864a4df30520&redirect_uri=${encodeURIComponent(local)}&response_type=code&scope=${scope}&state=${state}#wechat_redirect`
  window.location.replace(oauthUrl)
}

/**
 * appidCode初始化 type=1,返回appidCode; type=2,返回appid
 */
const appidCodeArr = [{
  code: 'WANG_YUE',
  name: '考拉',
  appid: 'wxd547642416441d89',
  appCode: 'WY'
}, {
  code: 'FEI_ZHANG',
  name: '飞掌',
  appid: 'wx1c5d0ad4a1016890',
  appCode: 'FZ'
}, {
  code: 'YE_SHE_HUI',
  name: '夜奢汇精油SPA',
  appid: 'wx99cbbe079f30543a',
  appCode: 'YSH'
}]
const appidCodeInit = (appCode = 'WY', type = 1) => {
  if (appCode) {
    const urlAppId = appidCodeArr.filter(x => x.appCode === appCode)[0].appid
    const appidCode = appidCodeArr.filter(x => x.appCode === appCode)[0].code
    if (type === 1) {
      return appidCode
    } else {
      return urlAppId
    }
  } else {
    if (type === 1) {
      return 'WANG_YUE'
    } else {
      return 'wxd547642416441d89'
    }
  }
}

const countSeconds = (params = { time: 60, timeText: 60, timer: null }) => {
  clearInterval(params.timer)
  if (params.time > 0 ) {
    params.timer = setInterval(() => {
      params.time--
      params.timeText = params.time
      if (params.time < 1) {
        clearInterval(params.timer)
      }
    }, 1000)
  }
}


const addUrlParam = (url, name, value) => {
  let newUrl = url
  newUrl += (newUrl.indexOf('?') == -1 ? '?' : '&')
  newUrl += encodeURIComponent(name) + '=' + encodeURIComponent(value + '')
  return newUrl
}

const jsonToUrl = (url, param) => {
  let result = url
  for (let key in param) {
    result = addUrlParam(result, key, param[key]); //传参
  }
  return result
}

// 检查是否是新包
const checkIsNewPackage = async () => {
  return utilIsApp() && await checkAppHandle('containCookieVersion')
}

//隐藏手机号码中间4位
const hideMobileNunmber = (v:string) => {
  if (/1[3-9]\d{9}/.test(v)) {
    return v.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
  }
  return v
}

// 米转换km
const utilMToKm = (m: number) => {
  if (!m) return 0
  return parseFloat((m / 1000).toString()).toFixed(1)
}

// 判断是否有空值
const hasEmptyObj = (json: object, key: string) => {
  let result = false
  for (const jsonKey in json) {
    if (jsonKey !== key) {
      result = (json[jsonKey] === null || json[jsonKey] === undefined || json[jsonKey] === 'null' || json[jsonKey] === 'undefined' || json[jsonKey] === '')
    }
  }
  return result
}

// 金额转换
const moneyTranslate = (num = 0, point = 2) => {
  if (!num) return 0
  if (Number.isInteger(num)) {
    return num
  } else {
    return num.toFixed(point)
  }
}

const formatMoneyByCent = (num = 0) => {
  if (!num) return 0
  return parseFloat((num / 100).toFixed(2))
}

const hideUserName = (name: string) => {
  if (!name) return ''
  let newName = ''
  if (name.length > 2) {
    newName = name.substring(0, 1) + '*' + name.substring(name.length - 1, name.length)
  } else {
    newName = name.substring(0, 1) + '*'
  }
  return newName
}

const base64ToBuffer = (base64) => {
  const binaryString = window.atob(base64.split(';base64,')[1]);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

const base64ToBlob = (base64, mimeType = 'application/octet-stream') => {
  const buffer = base64ToBuffer(base64);
  return new Blob([buffer], { type: mimeType });
}

const base64ToFile = (base64, filename, mimeType = 'image/png') => {
  const blob = base64ToBlob(base64, mimeType);
  return new File([blob], filename, { type: mimeType });
}

// 根据所传版本号判断当前版本app是否符合某个新需求功能
const getNowAppVersionIsMore = (ver) => {
  if (!utilIsApp()) return true
  const storeData = store()
  const version = storeData.appData.version
  if (!ver || version === undefined || version === null || version === '') return false // 没传值或者版本不存在
  // 根据传值判断版本号是否大于本地版本，版本号比如3.18.0
  return version.split('.').map(Number).every((v, i) => v >= ver.split('.').map(Number)[i])
}

// 金额转换
const moneyTranslateFixed = (num = 0) => {
  if (!num) return '0.00'
  return num.toFixed(2)
}


// 判断是否过期
const checkDateIsInUse = (date: number) => {
  if (!date) return false
  return new Date().getTime() < new Date(date).getTime()
}

// 获取挡墙url所有参数
const getAllUrlParams = (urlPath: string) => {
  if (!urlPath) return {}
  // 使用新窗口的location对象来获取当前页面的URL
  let url = urlPath
  // 创建一个URLSearchParams实例
  let params = new URLSearchParams(url.split('?')[1])
  let result = {}
  for (let pair of params.entries()) {
    if (result[pair[0]]) {
      result[pair[0]] = Array.isArray(result[pair[0]]) ? result[pair[0]] : [result[pair[0]]]
      result[pair[0]].push(pair[1])
    } else {
      result[pair[0]] = pair[1]
    }
  }
  return result
}

const formatHourByMinute = (num = 0, point = 1) => {
  if (!num) return 0
  return parseFloat((num / 60).toFixed(point))
}

const formatPercent = (num = 0, point = 2) => {
  if (!num) return 0
  return parseFloat((num * 100).toFixed(point))
}

export {
  utilGetFpPromise,
  utilGetImage,
  utilGetUrlData,
  utilGoBack,
  goBack,
  utilIsIos,
  utilIsAndroid,
  utilPush,
  utilMath,
  utilResetArr,
  utilDateFormat,
  utilFilterName,
  utilResetObj,
  utilIsFlutter,
  utilIsApp,
  utilGetDictList,
  utilShowImage,
  utilRegTel,
  utilShowVideoUrl,
  utilShowImageUrl,
  utilMaxUpload,
  copyText,
  utilFilterCardNumber,
  utilGoLogin,
  utilGoHome,
  encryptionPwd,
  utilReplace,
  checkAppHandle,
  utilGoMoreLevel,
  utilInterceptUrl,
  getNavHeight,
  utilGetNavOfApp,
  editUrlOfIos,
  dateLongAgo,
  xssFormat,
  getAppLocation,
  filterName,
  dateDifference,
  dateFormat,
  timeCount,
  parseQueryString,
  toCopy,
  utilIsPC,
  getPercent,
  loadScript,
  locationPush,
  httpFormat,
  isWeiXin,
  getWxCode,
  appidCodeInit,
  jsonToUrl,
  getContainerHeight,
  countSeconds,
  checkIsNewPackage,
  hideMobileNunmber,
  utilGoLogOut,
  utilMToKm,
  hasEmptyObj,
  moneyTranslate,
  hideUserName,
  base64ToFile,
  getNowAppVersionIsMore,
  moneyTranslateFixed,
  checkDateIsInUse,
  formatMoneyByCent,
  getAllUrlParams,
  formatHourByMinute,
  formatPercent
};
